import React, { useCallback, useEffect, useMemo, useState } from "react";
import { pageGutterPx } from "../../core/breakpoints";
import { C, DEFAULT_DISPLAY_TIMEZONE } from "../../core/constants";
import { APP_FONT_SIZE } from "../../core/theme";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { normalizeAuthUser } from "../../config/runtime";
import { updateAccountProfile } from "../account/api";
import { useDisplayTimezone } from "../timezone/TimezoneProvider";
import { buildFormInputStyle } from "../../core/formFields";
import NativePickerInput from "../../core/NativePickerInput";
import Button from "../../ui/Button";
import CityPicker from "../../ui/CityPicker";

export default function SettingsPage({ acc, authContext, t, locale }) {
  const fs = APP_FONT_SIZE;
  const gutter = pageGutterPx(useIsMobile());
  const accountUser = authContext.user || {};
  const emailVerified = Boolean(accountUser.emailVerified);
  const emailVerifiedAt = accountUser.emailVerifiedAt || "";
  const trustedGoogleAccount = Boolean(accountUser.trustedGoogleAccount);
  const emailVerifiedAtLabel = useMemo(() => {
    if (trustedGoogleAccount) return t("account_email_verified_via_google");
    if (!emailVerified) return t("account_email_verified_by_email_link");
    if (!emailVerifiedAt) return t("account_email_verified_on_unknown");

    const parsed = new Date(emailVerifiedAt);
    if (Number.isNaN(parsed.getTime())) return t("account_email_verified_on_unknown");
    const formatted = new Intl.DateTimeFormat(locale || "en", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(parsed);
    return t("account_email_verified_on").replace("%{date}", formatted);
  }, [trustedGoogleAccount, emailVerified, emailVerifiedAt, locale, t]);
  const [fullName, setFullName] = useState(
    accountUser.fullName || accountUser.email?.split("@")[0] || "",
  );
  const [birthDate, setBirthDate] = useState(accountUser.birthDate || "");
  const [birthTime, setBirthTime] = useState(accountUser.birthTime || "");
  const [birthPlace, setBirthPlace] = useState(accountUser.birthPlace || "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const {
    timezone,
    setTimezone,
    supportedTimezones,
    getGmtLabel,
    getGmtLabelForTimezone,
  } = useDisplayTimezone();
  const [pendingTimezone, setPendingTimezone] = useState(timezone || DEFAULT_DISPLAY_TIMEZONE);
  useEffect(() => {
    setPendingTimezone(timezone || DEFAULT_DISPLAY_TIMEZONE);
  }, [timezone]);
  const timezoneOptions = useMemo(() => {
    const now = new Date();
    return supportedTimezones
      .map((zone) => ({
        zone,
        offset: getOffsetMinutes(zone, now),
        label: `${zone} (${getGmtLabelForTimezone(zone, locale, now)})`,
      }))
      .sort((a, b) => {
        if (a.offset !== b.offset) return b.offset - a.offset;
        return a.zone.localeCompare(b.zone);
      });
  }, [supportedTimezones, getGmtLabelForTimezone, locale]);

  function getOffsetMinutes(timeZone, date = new Date()) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts = formatter.formatToParts(date);
    const map = {};
    parts.forEach((part) => {
      if (part.type !== "literal") map[part.type] = part.value;
    });
    const asUtcMs = Date.UTC(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      Number(map.hour),
      Number(map.minute),
      Number(map.second),
    );
    return Math.round((asUtcMs - date.getTime()) / 60000);
  }

  const inputStyle = buildFormInputStyle({ fontSize: fs });

  const saveProfile = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setSaveError("");
    setSaveNotice("");
    setTimezone(pendingTimezone);

    try {
      const response = await updateAccountProfile({
        user: {
          fullname: fullName.trim(),
          birth_date: birthDate || null,
          birth_time: birthTime || null,
          birthplace: birthPlace || null,
        },
      });

      const normalizedResponseUser = normalizeAuthUser(response.user);
      authContext.user = {
        ...(authContext.user || {}),
        ...(normalizedResponseUser || {}),
        fullName: fullName.trim() || normalizedResponseUser?.fullName || authContext.user?.fullName || "",
        birthDate: birthDate || "",
        birthTime: birthTime || "",
        birthPlace: birthPlace || "",
      };
      setFullName(authContext.user?.fullName || accountUser.email?.split("@")[0] || "");
      setBirthDate(authContext.user?.birthDate || "");
      setBirthTime(authContext.user?.birthTime || "");
      setBirthPlace(authContext.user?.birthPlace || "");
      setSaveNotice(t("settings_saved"));
    } catch (error) {
      setSaveError(error.message || t("save_failed"));
    } finally {
      setSaving(false);
    }
  }, [saving, fullName, birthDate, birthTime, birthPlace, authContext, accountUser.email, t, pendingTimezone, setTimezone]);

  return (
    <div style={{ padding: gutter, maxWidth: 640 }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, fontWeight: 400, color: C.textPrimary, marginBottom: 32 }}>{t("account_settings")}</h1>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("full_name").toUpperCase()}</div>
        <input type="text" value={fullName} onChange={(event) => setFullName(event.target.value)} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("email").toUpperCase()}</div>
        <div style={{ position: "relative" }}>
          <input
            type="email"
            value={accountUser.email || ""}
            readOnly
            disabled
            style={{ ...inputStyle, opacity: 0.8, cursor: "not-allowed", paddingRight: 132 }}
          />
          <span
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: emailVerified ? "#c7ffe8" : "#ffe0a3",
              background: emailVerified ? "rgba(94,240,216,0.18)" : "rgba(240,192,96,0.18)",
              border: emailVerified ? "1px solid rgba(94,240,216,0.4)" : "1px solid rgba(240,192,96,0.45)",
              pointerEvents: "none",
            }}
          >
            {emailVerified ? t("status_verified_short") : t("status_unverified_short")}
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>{emailVerifiedAtLabel}</span>
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: C.textMuted }}>{t("account_email_locked_notice")}</div>
      </div>
      <div style={{ marginBottom: 20, minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("birth_date").toUpperCase()}</div>
        <NativePickerInput
          type="date"
          value={birthDate}
          onChange={(event) => setBirthDate(event.target.value)}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 20, minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("birth_time").toUpperCase()}</div>
        <NativePickerInput
          type="time"
          value={birthTime}
          onChange={(event) => setBirthTime(event.target.value)}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("birth_place").toUpperCase()}</div>
        <CityPicker
          value={birthPlace}
          onChange={setBirthPlace}
          locale={locale}
          acc={acc}
          inputStyle={inputStyle}
          placeholder={t("wz_birthplace_placeholder")}
          maxVisibleItems={4}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>
          {t("settings_timezone_display_label").toUpperCase()}
        </div>
        <select
          value={pendingTimezone || DEFAULT_DISPLAY_TIMEZONE}
          onChange={(event) => {
            const nextTimezone = event.target.value;
            setPendingTimezone(nextTimezone);
          }}
          style={{
            ...inputStyle,
            appearance: "none",
            cursor: "pointer",
            background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
            border: `1px solid ${acc.primary}55`,
            boxShadow: `0 0 0 1px ${acc.primary}22 inset`,
            paddingRight: 38,
            backgroundImage: "linear-gradient(45deg, transparent 50%, rgba(237,234,248,0.8) 50%), linear-gradient(135deg, rgba(237,234,248,0.8) 50%, transparent 50%)",
            backgroundPosition: "calc(100% - 18px) calc(50% - 2px), calc(100% - 12px) calc(50% - 2px)",
            backgroundSize: "6px 6px, 6px 6px",
            backgroundRepeat: "no-repeat",
          }}
        >
          {timezoneOptions.map(({ zone, label }) => (
            <option key={zone} value={zone} style={{ background: "#0c1230", color: C.textPrimary }}>
              {label}
            </option>
          ))}
        </select>
        <div style={{ marginTop: 8, fontSize: 12, color: C.textMuted }}>
          {t("settings_timezone_display_hint")} {getGmtLabel(locale)}
        </div>
      </div>

      {saveError && <div style={{ marginBottom: 12, fontSize: 12, color: "#ff9ca8" }}>{saveError}</div>}
      {saveNotice && <div style={{ marginBottom: 12, fontSize: 12, color: acc.primary }}>{saveNotice}</div>}
      <Button
        variant="primary"
        acc={acc}
        onClick={saveProfile}
        style={{ marginTop: 8, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
      >
        {saving ? t("saving") : t("save_changes")}
      </Button>
    </div>
  );
}
